export type SdkConsoleLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent'

export interface SdkLoggingOptions {
    readonly consoleLevel?: SdkConsoleLogLevel
}

const CONSOLE_LEVEL_RANK: Record<SdkConsoleLogLevel, number> = {
    trace: 0,
    debug: 1,
    info: 2,
    warn: 3,
    error: 4,
    silent: 5,
}

const DEFAULT_CONSOLE_LEVEL: SdkConsoleLogLevel = 'debug'

let currentConsoleLevel: SdkConsoleLogLevel = DEFAULT_CONSOLE_LEVEL

function isSdkConsoleLogLevel(value: unknown): value is SdkConsoleLogLevel {
    return typeof value === 'string' && value in CONSOLE_LEVEL_RANK
}

export function configureSdkLogging(logging?: SdkLoggingOptions): void {
    if (logging?.consoleLevel == null) {
        currentConsoleLevel = DEFAULT_CONSOLE_LEVEL
        return
    }
    if (!isSdkConsoleLogLevel(logging.consoleLevel)) {
        return
    }
    currentConsoleLevel = logging.consoleLevel
}

export function getSdkLoggingOptions(): Required<SdkLoggingOptions> {
    return { consoleLevel: currentConsoleLevel }
}

export function shouldLogToConsole(level: Exclude<SdkConsoleLogLevel, 'silent'>): boolean {
    return CONSOLE_LEVEL_RANK[level] >= CONSOLE_LEVEL_RANK[currentConsoleLevel]
}

function writeConsole(level: Exclude<SdkConsoleLogLevel, 'silent'>, args: readonly unknown[]): void {
    if (!shouldLogToConsole(level)) {
        return
    }

    switch (level) {
        case 'trace':
        case 'debug':
            console.debug(...args)
            break
        case 'info':
            console.info(...args)
            break
        case 'warn':
            console.warn(...args)
            break
        case 'error':
            console.error(...args)
            break
    }
}

export const sdkConsole = {
    trace(...args: unknown[]): void {
        writeConsole('trace', args)
    },
    debug(...args: unknown[]): void {
        writeConsole('debug', args)
    },
    info(...args: unknown[]): void {
        writeConsole('info', args)
    },
    warn(...args: unknown[]): void {
        writeConsole('warn', args)
    },
    error(...args: unknown[]): void {
        writeConsole('error', args)
    },
}
